/**
 * Shared board-grouping engine for BOTH tracker board types (free-form items
 * and briefs). A board picks a `GroupingSpec` for the current "group by" choice
 * and calls `buildGroups(items, spec, toRow)` to turn its rows into the ordered
 * `BoardGroup[]` the table renders — buckets in a fixed order, plus a trailing
 * (or leading) catch-all for rows that match no bucket.
 *
 * Grouping is intentionally limited to CATEGORICAL columns (status, checkbox,
 * people, brief type, project) plus user-defined Custom groups — columns with a
 * bounded set of values. `bucketIdsFor` returns the bucket id(s) a row belongs
 * to: 0 = catch-all, 1 = normal, or MANY (a People/Owner row shows under each of
 * its assignees).
 *
 * `editable` marks groupings where dragging a row between groups (or adding a
 * row into one) is meaningful AND the stored value equals the bucket id — only
 * Status and Custom. Checkbox (stored boolean ≠ id) and People (stored array)
 * and the read-only Project/Brief-type groupings are view-only: change the row's
 * cell and it re-buckets.
 */
import { i18n } from '@/shared/i18n';

/** Group-by sentinel selecting the user-defined Custom groups. */
// eslint-disable-next-line i18next/no-literal-string -- group-by sentinel
export const CUSTOM_GROUP_BY = '__custom__';
/** Reserved key in customColumnValues holding a row's Custom group id. */
// eslint-disable-next-line i18next/no-literal-string -- reserved key in the stored column values
export const GROUP_VALUE_KEY = '__group__';
/** Id + color of the synthetic catch-all bucket. */
// eslint-disable-next-line i18next/no-literal-string -- bucket id sentinel
export const NO_GROUP_ID = '__none__';
export const NO_GROUP_COLOR = '#94a3b8';

/** Checkbox-grouping bucket ids (also the labels' basis). */
/* eslint-disable i18next/no-literal-string -- bucket ids */
export const CHECKBOX_CHECKED_ID = 'checked';
export const CHECKBOX_UNCHECKED_ID = 'unchecked';
/* eslint-enable i18next/no-literal-string */

export interface GroupBucket {
  id: string;
  label: string;
  color?: string;
}

export interface GroupingSpec<T> {
  /** Named buckets, rendered in this order. */
  buckets: GroupBucket[];
  /** Bucket id(s) a row belongs to. `[]` → the catch-all. */
  bucketIdsFor: (item: T) => string[];
  noneLabel: string;
  noneColor: string;
  nonePlacement: 'top' | 'bottom';
  /** Can rows be dragged between / created into groups? */
  editable: boolean;
  /** customColumnValues key to write on drag/add (editable groupings only). */
  key?: string;
}

export interface BuiltGroup<R> {
  id: string;
  title: string;
  color?: string;
  items: R[];
  itemCount: number;
}

/**
 * Bucket `items` per `spec`, preserving input order within each bucket (callers
 * pass already-sorted rows). Rows matching several buckets appear in each
 * (multi-membership); rows matching none fall into the catch-all, which is
 * omitted when empty.
 */
export function buildGroups<T, R>(
  items: T[],
  spec: GroupingSpec<T>,
  toRow: (item: T) => R,
): BuiltGroup<R>[] {
  const byBucket = new Map<string, T[]>(spec.buckets.map((b) => [b.id, []]));
  const none: T[] = [];

  for (const item of items) {
    let placed = false;
    for (const id of spec.bucketIdsFor(item)) {
      const bucket = byBucket.get(id);
      if (bucket) {
        bucket.push(item);
        placed = true;
      }
    }
    if (!placed) none.push(item);
  }

  const named: BuiltGroup<R>[] = spec.buckets.map((b) => {
    const rows = byBucket.get(b.id) ?? [];
    return {
      id: b.id,
      title: b.label,
      color: b.color,
      items: rows.map(toRow),
      itemCount: rows.length,
    };
  });

  if (none.length === 0) return named;
  const noneGroup: BuiltGroup<R> = {
    id: NO_GROUP_ID,
    title: spec.noneLabel,
    color: spec.noneColor,
    items: none.map(toRow),
    itemCount: none.length,
  };
  return spec.nonePlacement === 'top' ? [noneGroup, ...named] : [...named, noneGroup];
}

/**
 * Bucket id → row count, over rows that search and filters have NOT narrowed.
 * A bucket rendering no rows reads differently depending on whether it never
 * held any or a filter hid them, and the narrowed list cannot tell them apart.
 * Counting only, so no row objects are built for a result nobody renders.
 */
export function countGroups<T>(items: T[], spec: GroupingSpec<T>): Map<string, number> {
  return new Map(buildGroups(items, spec, () => null).map((g) => [g.id, g.itemCount]));
}

// ─────────────────────────── Spec builders ───────────────────────────
// Row shape shared by both boards — everything grouped by a custom column
// reads its value out of `customColumnValues`.
interface HasCustomValues {
  customColumnValues?: Record<string, unknown> | null;
}

const asStringId = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

/** Single-value grouping: each row lands in at most one bucket. */
export function singleValueSpec<T>(args: {
  buckets: GroupBucket[];
  getId: (item: T) => string | null | undefined;
  noneLabel: string;
  noneColor?: string;
  nonePlacement?: 'top' | 'bottom';
  editable?: boolean;
  key?: string;
}): GroupingSpec<T> {
  return {
    buckets: args.buckets,
    bucketIdsFor: (item) => {
      const id = args.getId(item);
      return id ? [id] : [];
    },
    noneLabel: args.noneLabel,
    noneColor: args.noneColor ?? NO_GROUP_COLOR,
    nonePlacement: args.nonePlacement ?? 'bottom',
    editable: args.editable ?? false,
    key: args.key,
  };
}

/** Multi-value grouping (People/Owner): a row can land in several buckets. */
export function multiValueSpec<T>(args: {
  buckets: GroupBucket[];
  getIds: (item: T) => string[];
  noneLabel: string;
  noneColor?: string;
  nonePlacement?: 'top' | 'bottom';
}): GroupingSpec<T> {
  return {
    buckets: args.buckets,
    bucketIdsFor: args.getIds,
    noneLabel: args.noneLabel,
    noneColor: args.noneColor ?? NO_GROUP_COLOR,
    nonePlacement: args.nonePlacement ?? 'bottom',
    editable: false,
  };
}

/** Status column → its options; drag/create moves rows (id == stored value). */
export function statusColumnSpec(column: {
  id: string;
  statusOptions?: GroupBucket[];
}): GroupingSpec<HasCustomValues> {
  return singleValueSpec({
    buckets: column.statusOptions ?? [],
    getId: (item) => asStringId(item.customColumnValues?.[column.id]),
    noneLabel: i18n.t('trackers:trackersBoard.kanbanView.noStatus'),
    editable: true,
    key: column.id,
  });
}

/** Custom user-defined groups; the default free-form grouping. */
export function customGroupSpec(customGroups: GroupBucket[]): GroupingSpec<HasCustomValues> {
  return singleValueSpec({
    buckets: customGroups,
    getId: (item) => asStringId(item.customColumnValues?.[GROUP_VALUE_KEY]),
    noneLabel: i18n.t('trackers:trackersBoard.grouping.ungrouped'),
    nonePlacement: 'top',
    editable: true,
    key: GROUP_VALUE_KEY,
  });
}

/** Checkbox column → Checked / Unchecked (view-only: stored boolean ≠ id). */
export function checkboxColumnSpec(column: { id: string }): GroupingSpec<HasCustomValues> {
  return {
    buckets: [
      {
        id: CHECKBOX_CHECKED_ID,
        label: i18n.t('trackers:trackersBoard.checkboxCustomCell.checked'),
        color: '#10b981',
      },
      {
        id: CHECKBOX_UNCHECKED_ID,
        label: i18n.t('trackers:trackersBoard.checkboxCustomCell.unchecked'),
        color: NO_GROUP_COLOR,
      },
    ],
    bucketIdsFor: (item) => [
      item.customColumnValues?.[column.id] ? CHECKBOX_CHECKED_ID : CHECKBOX_UNCHECKED_ID,
    ],
    noneLabel: '',
    noneColor: NO_GROUP_COLOR,
    nonePlacement: 'bottom',
    editable: false,
  };
}

/** People/Owner column → one bucket per user; multi-owner rows show in each. */
export function peopleColumnSpec(
  column: { id: string },
  users: GroupBucket[],
): GroupingSpec<HasCustomValues> {
  return multiValueSpec({
    buckets: users,
    getIds: (item) => {
      const raw = item.customColumnValues?.[column.id];
      return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
    },
    noneLabel: i18n.t('trackers:trackersBoard.grouping.noOwner'),
  });
}
